import { makeObservable, observable, runInAction } from "mobx"
import { Address, PublicClient } from "viem"
import { Config } from "wagmi"
import { getPublicClient } from "wagmi/actions"

import type { ApiService } from "@/domains/api/api-service"
import type { EpochNamespace } from "@/domains/epochs/epoch-namespace-factory"
import type { FeatureFlagsNamespace } from "@/domains/feature-flags/feature-flags-namespace-factory"
import type { GaugeNamespace, IEmissionsConfig, IGaugeRepository } from "@/domains/gauge"
import type { LockVenammQuotesNamespace } from "@/domains/lock-venamm-quotes"
import type { ManagerNamespace } from "@/domains/manager/types"
import type { ManualClV1Namespace } from "@/domains/thick-v1/manual-cl-namespace-factory"
import type { ManualClV2Namespace } from "@/domains/thick-v2/manual-cl-namespace-factory"
import type { SwapNamespace } from "@/domains/swap"
import type { StatsEqualizerResponse } from "@/domains/validators"
import type { VoteNamespace } from "@/domains/vote"
import type { StrategyManagerNamespace } from "@/domains/vote-optimizer/strategy-manager-namespace-factory"
import { appConfig } from "@/config/app-config"
import type { ChainConfig } from "@/domains/core/types"
import { safelyFetchGraphQl } from "@/domains/api/safely-fetch-graphql"
import { BlogNamespaceFactory } from "@/domains/blog"
import {
  ClFarmApiTransport,
  ClFarmOnChainTransport,
  ClFarmOnChainTransportAddresses,
  ClFarmOrchestrator,
  ClFarmOrchestratorFactory,
  ClFarmRepository,
} from "@/domains/cl-farm"
import { ClFarmsPageStore } from "@/domains/cl-farms-page/cl-farms-page-store"
import { LocalStorageBackend, UrlParamsBackend } from "@/domains/common"
import { IChainConfig } from "@/domains/core/interfaces"
import { LcAddress } from "@/domains/core/lc-address"
import { container, TYPES } from "@/domains/di"
import { DocsNamespaceFactory } from "@/domains/docs"
import { getEpochGraphQlBaseUrl } from "@/domains/epochs/epoch-config"
import { EpochGraphQlService } from "@/domains/epochs/epoch-graphql-service"
import { EpochNamespaceFactory } from "@/domains/epochs/epoch-namespace-factory"
import { EventBus, IEventBus } from "@/domains/events"
import { FeatureFlagsNamespaceFactory } from "@/domains/feature-flags/feature-flags-namespace-factory"
import { GaugeNamespaceFactory } from "@/domains/gauge"
import { IncentivesPageStore } from "@/domains/incentives-page/incentives-page-store"
import {
  IPublicClientGetter,
  OrchestratedQuery,
  OrchestratedQueryStore,
  PublicClientGetterAdapter,
  SequentialChunkedRpcStrategy,
} from "@/domains/library"
import { LiquidityPageStore } from "@/domains/liquidity-page/liquidity-page-store"
import { LockOnChainTransportAddresses } from "@/domains/lock"
import { LockPageStore } from "@/domains/lock-page"
import { SelectedLockOrchestrator } from "@/domains/lock-page/selected-lock-orchestrator"
import { WithdrawOrchestratorFactory } from "@/domains/lock-page/withdraw-orchestrator-factory"
import {
  ByNftGroupingStrategy,
  ByTokenGroupingStrategy,
  LockRewardRepository,
  LockRewardsOnChainTransport,
  LockRewardsOnChainTransportAddresses,
  NftSelectionStrategy,
  TokenSelectionStrategy,
} from "@/domains/lock-rewards"
import { LockVenammQuotesNamespaceFactory } from "@/domains/lock-venamm-quotes"
import { LockNamespaceFactory } from "@/domains/lock/lock-namespace-factory"
import { LockOnChainTransport } from "@/domains/lock/lock-on-chain-transport"
import { LockNamespace } from "@/domains/lock/types"
import { ConsoleLogStrategy, LogStore } from "@/domains/log/log-store"
import { ManagerNamespaceFactory } from "@/domains/manager/manager-namespace-factory"
import { ManualClPageStore } from "@/domains/manual-cl-page/manual-cl-page-store"
import { getThickV1GraphQlUrl } from "@/domains/thick-v1/manual-cl-config"
import { ManualClGraphQlService as ManualClV1GraphQlService } from "@/domains/thick-v1/manual-cl-graphql-service"
import { ManualClV1NamespaceFactory } from "@/domains/thick-v1/manual-cl-namespace-factory"
import { getThickV2GraphQlUrl } from "@/domains/thick-v2/manual-cl-config"
import {
  ManualClGraphQlService as ManualClV2GraphQlService,
  type RecentSwapActivity,
} from "@/domains/thick-v2/manual-cl-graphql-service"
import { ManualClV2NamespaceFactory } from "@/domains/thick-v2/manual-cl-namespace-factory"
import {
  MinimalPoolApiTransport,
  MinimalPoolOnChainTransport,
  MinimalPoolOnChainTransportAddresses,
  MinimalPoolOrchestrator,
  MinimalPoolOrchestratorFactory,
  MinimalPoolRepository,
} from "@/domains/minimal-pool"
import { ZapOrchestrator } from "@/domains/minimal-pools-zap/zap-orchestrator"
import { ZapOrchestratorFactory } from "@/domains/minimal-pools-zap/zap-orchestrator-factory"
import { OnChainService } from "@/domains/on-chain"
import { PendingCreationStore } from "@/domains/pending-creation"
import { Pools } from "@/domains/pools/pools"
import { HeroBlockStatsStore } from "@/domains/landing-page/stores/hero-block-stats-store"
import { LiveActivityStore } from "@/domains/landing-page/stores/live-activity-store"
import type { LandingActivityRow } from "@/domains/landing-page/interfaces"
import {
  HeroEmissionsStore,
  type HeroEmissionsAnalyticsData,
} from "@/domains/landing-page/stores/hero-emissions-store"
import { ScaleStatsApiTransport, ScaleStatsOrchestrator, ScaleStatsStore } from "@/domains/scale-stats"
import { SettingsStore } from "@/domains/settings/settings-store"
import { SwapNamespaceFactory } from "@/domains/swap"
import { TokenApiTransport, TokenOrchestrator, TokenRepository } from "@/domains/tokens"
import { QuickSelectTokenStoreFactory } from "@/domains/tokens/factories/quick-select-token-store-factory"
import { QuickSelectTokenStore } from "@/domains/tokens/quick-select-token-store"
import { ETH, SCALE, VESTED, WETH } from "@/domains/tokens/static-tokens"
import { TokenOnChainTransport } from "@/domains/tokens/token-onchain-transport"
import { TokenOrchestratorFactory } from "@/domains/tokens/token-orchestrator-factory"
import { SystemTokenConfig } from "@/domains/tokens/token-repository"
import { TokenStore } from "@/domains/tokens/token-store"
import { isTokenBlacklistChecker } from "@/domains/tokens/tokens-blacklist"
import { VenammNamespace, VenammNamespaceFactory } from "@/domains/venamm"
import { VoteNamespaceFactory } from "@/domains/vote"
import { StrategyManagerNamespaceFactory } from "@/domains/vote-optimizer/strategy-manager-namespace-factory"
import { VotePageNamespaceFactory } from "@/domains/vote-page"
import { VoteOnChainTransport, VoteOnChainTransportAddresses } from "@/domains/vote/vote-on-chain-transport"
import { WalletConnectionStore, WalletMenuStore } from "@/domains/wallet"

export type ScaleAppContractAddresses = {
  clFarmLensAddress: Address
  equalensMultiBalance: Address
  equalens26: Address
  infoBribeByNft: Address
  scalensDefaultInfoPairs?: Address
  scalensUserInfoPairs?: Address
  scalensUserManageMinimalPoolInfo?: Address
  veInfo2: Address
  veToken: Address
  voter: Address
  superVoter: Address
  venAmm: Address
}

type InitializeScaleApplicationParams = {
  apiService: ApiService
  safeAddress: Address
  chainSlug: string
  scaleAppContractAddresses: ScaleAppContractAddresses
  config: Config
  chainId: number
  systemTokenAddresses: SystemTokenConfig
  routerAddress: Address
  emissionsConfig: IEmissionsConfig
}

export class ScaleApp {
  private static instance: ScaleApp | null = null
  private readonly apiService: ApiService
  private readonly scaleAppContractAddresses: ScaleAppContractAddresses
  private readonly chainSlug: string
  private readonly config: Config
  public readonly OrchestratedQueryStore: OrchestratedQueryStore
  public readonly EventBus: EventBus
  private readonly chainId: number

  public readonly ClFarms: {
    Orchestrator: ClFarmOrchestrator
    Repository: ClFarmRepository
  }
  public readonly Gauge: {
    Repository: any
    Orchestrator: any
  }
  public readonly Lock: LockNamespace
  public readonly LockVenammQuotes: LockVenammQuotesNamespace
  public readonly Manager: ManagerNamespace
  public readonly Venamm: VenammNamespace
  public readonly LockRewards: {
    Repository: LockRewardRepository
    Strategies: {
      TokenGrouping: ByTokenGroupingStrategy
      TokenSelection: TokenSelectionStrategy
      NftGrouping: ByNftGroupingStrategy
      NftSelection: NftSelectionStrategy
    }
  }
  public readonly MinimalPools: {
    Orchestrator: MinimalPoolOrchestrator
    Repository: MinimalPoolRepository
    ZapOrchestrator: ZapOrchestrator
  }
  public readonly Pages: {
    Incentives: IncentivesPageStore
    ClFarms: ClFarmsPageStore
    Liquidity: LiquidityPageStore
    Lock: LockPageStore
    Vote: any
    /**
     * Canonical Manual CL page store for thick-v1 (filters, sort, view
     * toggle, etc.).
     */
    ManualClV1: ManualClPageStore
    /**
     * Canonical Manual CL page store for thick-v2. The `"v2"` constructor
     * argument partitions localStorage so v1 and v2 filters never collide.
     */
    ManualClV2: ManualClPageStore
  }
  public readonly PendingCreation: PendingCreationStore
  public readonly Vote: VoteNamespace
  public readonly VoteOptimizer: StrategyManagerNamespace
  public readonly Stats: ScaleStatsStore
  public readonly Tokens: {
    Orchestrator: TokenOrchestrator
    Repository: TokenRepository
    QuickSelect: QuickSelectTokenStore
    /**
     * Governance token (SCALE) - accessible as TokenStore instance
     * This token is in the repository and receives price/balance updates
     */
    readonly governance: TokenStore
    /**
     * Vested token (veSCALE) - accessible as TokenStore instance
     * This is a reference token, NOT in the repository (cannot be swapped)
     */
    readonly vested: TokenStore
    /**
     * Native token (ETH) - accessible as TokenStore instance
     * This is a reference token, NOT in the repository (not an ERC20)
     */
    readonly native: TokenStore
    /**
     * Wrapped native token (WETH) - accessible as TokenStore instance
     * This token is in the repository and receives price/balance updates
     */
    readonly wrappedNative: TokenStore
  }
  /**
   * Chain configuration namespace
   * Provides access to commonly used chain config properties
   */
  public readonly Config: IChainConfig
  public readonly Swap: SwapNamespace
  public readonly Wallet: {
    Connection: WalletConnectionStore
    Menu: WalletMenuStore
  }
  public readonly Log: LogStore
  public readonly Settings: SettingsStore
  public readonly FeatureFlags: FeatureFlagsNamespace
  public readonly ManualClV1: ManualClV1Namespace
  public readonly ManualClV2: ManualClV2Namespace
  public readonly Epochs: EpochNamespace
  public readonly HeroBlockStats: HeroBlockStatsStore
  public readonly LiveActivity: LiveActivityStore
  public readonly Blog: ReturnType<typeof BlogNamespaceFactory.create>
  public readonly Docs: ReturnType<typeof DocsNamespaceFactory.create>

  private constructor(
    apiService: ApiService,
    safeAddress: Address,
    chainSlug: string,
    scaleAppContractAddresses: ScaleAppContractAddresses,
    config: Config,
    chainId: number,
    systemTokenAddresses: SystemTokenConfig,
    routerAddress: Address,
    private readonly emissionsConfig: IEmissionsConfig,
  ) {
    this.apiService = apiService
    this.chainSlug = chainSlug
    this.chainId = chainId
    this.config = config
    this.scaleAppContractAddresses = scaleAppContractAddresses

    // Create OrchestratedQueryStore for monitoring all orchestrated queries
    this.OrchestratedQueryStore = new OrchestratedQueryStore()

    // Create RPC strategy instances (singleton pattern)
    const chunkedRpcStrategy = new SequentialChunkedRpcStrategy()

    // Create EventBus for domain event coordination
    this.EventBus = new EventBus()

    const publicClientGetter = new PublicClientGetterAdapter(() => this.getPublicClient())

    // Create WalletConnectionStore for wallet state management
    const walletConnectionStore = new WalletConnectionStore(chainId, safeAddress, this.EventBus)

    // Create WalletMenuStore - will be initialized after Manager namespace is created
    // Placeholder for now
    this.Wallet = {
      Connection: walletConnectionStore,
      Menu: null as unknown as WalletMenuStore, // Will be set after Manager is created
    }

    // Create LogStore for centralized logging
    this.Log = new LogStore(new ConsoleLogStrategy())

    // Create storage backends
    const localStorageBackend = new LocalStorageBackend(window)
    const urlParamsBackend = new UrlParamsBackend(window)

    // Create SettingsStore for application settings
    this.Settings = new SettingsStore(localStorageBackend, urlParamsBackend, this.EventBus)
    this.Settings.initialize()

    // Create FeatureFlags namespace
    this.FeatureFlags = FeatureFlagsNamespaceFactory.create({
      storage: localStorageBackend,
      eventBus: this.EventBus,
    })

    // -- PAGES --
    // Note: LockPageStore needs WithdrawOrchestrator, which needs Lock.Repository
    // So we create a placeholder for now and update it after Lock namespace is created
    const manualClV1PageStore = new ManualClPageStore(
      localStorageBackend,
      urlParamsBackend,
      this.chainSlug,
      "v1",
    )
    const manualClV2PageStore = new ManualClPageStore(
      localStorageBackend,
      urlParamsBackend,
      this.chainSlug,
      "v2",
    )
    this.Pages = {
      Incentives: new IncentivesPageStore(localStorageBackend, urlParamsBackend, this.chainSlug),
      ClFarms: new ClFarmsPageStore(localStorageBackend, urlParamsBackend, this.chainSlug),
      Liquidity: new LiquidityPageStore(localStorageBackend, urlParamsBackend, this.chainSlug),
      Lock: {} as LockPageStore, // Placeholder - will be set after Lock namespace is created
      Vote: {} as any, // Placeholder - will be set after Vote namespace is created
      ManualClV1: manualClV1PageStore,
      ManualClV2: manualClV2PageStore,
    }
    this.Pages.Incentives.initialize()
    this.Pages.ClFarms.initialize()
    this.Pages.Liquidity.initialize()
    this.Pages.ManualClV1.initialize()
    this.Pages.ManualClV2.initialize()
    // LockPageStore is created after Lock namespace (see below), so initialize it after that

    this.PendingCreation = new PendingCreationStore()

    // Create OnChainService for blockchain operations
    const onChainService = new OnChainService(this.config, () => this.getPublicClient(), {
      equalensMultiBalance: this.scaleAppContractAddresses.equalensMultiBalance,
    })

    // Create TokenOnChainTransport with strategy
    const tokenOnChainTransport = new TokenOnChainTransport(onChainService, chunkedRpcStrategy)

    // -- TOKENS --
    const tokenNamespace = buildTokenNamespace(
      apiService,
      tokenOnChainTransport,
      systemTokenAddresses,
      this.OrchestratedQueryStore,
      this.Wallet.Connection,
      this.EventBus,
    )

    // Extend token namespace with direct accessors for system tokens
    this.Tokens = {
      ...tokenNamespace,
      // Governance token (SCALE) - from repository (gets price/balance updates)
      get governance(): TokenStore {
        return tokenNamespace.Repository.find(SCALE.address) ?? SCALE
      },
      // Vested token (veSCALE) - static reference (not in repository)
      get vested(): TokenStore {
        return VESTED
      },
      // Native token (ETH) - static reference (not in repository, not ERC20)
      get native(): TokenStore {
        return ETH
      },
      // Wrapped native token (WETH) - from repository (gets price/balance updates)
      get wrappedNative(): TokenStore {
        return tokenNamespace.Repository.find(WETH.address) ?? WETH
      },
    }
    this.Tokens.Orchestrator.startTokensSync(30000)

    // -- SWAP --
    // Create swap namespace after tokens are initialized
    this.Swap = SwapNamespaceFactory.create({
      chainId: this.chainId,
      orchestratedQueryStore: this.OrchestratedQueryStore,
      tokenRepository: tokenNamespace.Repository,
      storage: localStorageBackend,
      urlParams: urlParamsBackend,
      chainSlug: this.chainSlug,
      nativeToken: this.Tokens.native,
      wrappedNativeToken: this.Tokens.wrappedNative,
      walletStore: this.Wallet.Connection,
      eventBus: this.EventBus,
      config: this.config,
      routerAddress,
      getMinimalPoolRepository: () => this.MinimalPools?.Repository,
      getThickV1PoolRepository: () => this.ManualClV1?.Repository,
      getThickV2PoolRepository: () => this.ManualClV2?.Repository,
      settingsStore: this.Settings,
      getNativeTokenPrice: () => {
        // Get native token price from wrapped native token's best price
        return this.Tokens.wrappedNative.bestPrice
      },
      getSlippageBps: () => {
        return Math.round(this.Settings.slippage * 100) // Convert percentage to basis points
      },
    })

    // -- CONFIG --
    // Create config namespace for commonly used chain config properties
    this.Config = {
      slug: this.chainSlug,
      chainId: this.chainId,
      contracts: this.scaleAppContractAddresses,
    }

    // -- GAUGE (must be created before MinimalPools for bidirectional mapping) --
    // Get chain config from somewhere - we'll need to pass it in
    // For now, we'll create a placeholder and update it
    this.Gauge = {} as GaugeNamespace

    // -- VOTE --
    // Moved before LOCK (see above) to provide voteOnChainTransport

    // -- LOCK REWARDS --
    const lockRewardsOnChainTransportAddresses: LockRewardsOnChainTransportAddresses = {
      infoBribeByNft: this.scaleAppContractAddresses.infoBribeByNft
        ? new LcAddress(this.scaleAppContractAddresses.infoBribeByNft)
        : new LcAddress("0x0000000000000000000000000000000000000000"),
    }
    const lockRewardsNamespace = buildLockRewardsNamespace(config, lockRewardsOnChainTransportAddresses)
    this.LockRewards = {
      Repository: lockRewardsNamespace.Repository,
      Strategies: {
        TokenGrouping: new ByTokenGroupingStrategy(),
        TokenSelection: new TokenSelectionStrategy(),
        NftGrouping: new ByNftGroupingStrategy(),
        NftSelection: new NftSelectionStrategy(),
      },
    }

    // -- VENAMM (must be created before LOCK) --
    // Pass lock rewards transport to lock orchestrator (internal dependency)
    const lockOnChainTransportAddresses: LockOnChainTransportAddresses = {
      veInfo2: new LcAddress(this.scaleAppContractAddresses.veInfo2),
      veToken: new LcAddress(this.scaleAppContractAddresses.veToken),
      governanceToken: systemTokenAddresses.governance,
      vestedToken: this.scaleAppContractAddresses.veToken,
      venAmm: new LcAddress(this.scaleAppContractAddresses.venAmm),
    }

    // Create lockOnChainTransport instance (shared between Venamm and Lock)
    const governanceToken = this.Tokens.Repository.find(new LcAddress(systemTokenAddresses.governance))
    const governanceTokenDecimals = governanceToken?.decimals ?? 18
    const vestedTokenDecimals = 18

    const lockOnChainTransport = new LockOnChainTransport(
      config,
      lockOnChainTransportAddresses,
      governanceTokenDecimals,
      vestedTokenDecimals,
    )

    // Create Venamm base namespace first (supply tracking only - required by Lock)
    const venammBase = VenammNamespaceFactory.createBase({
      config,
      venAmmAddress: this.scaleAppContractAddresses.venAmm,
      lockOnChainTransport,
      tokenLookup: this.Tokens.Repository,
      orchestratedQueryStore: this.OrchestratedQueryStore,
      eventBus: this.EventBus,
    })

    // -- VOTE --
    // Create voteOnChainTransport instance (shared between Vote, Lock, and Manager)
    const voteOnChainTransportAddresses: VoteOnChainTransportAddresses = {
      equalens26: this.scaleAppContractAddresses.equalens26,
    }
    const voteOnChainTransport = new VoteOnChainTransport(config, chainId, voteOnChainTransportAddresses)

    this.Vote = VoteNamespaceFactory.create({
      eventBus: this.EventBus,
    })

    // -- LOCK --
    this.Lock = LockNamespaceFactory.create({
      config,
      eventBus: this.EventBus,
      lockRewardRepository: lockRewardsNamespace.Repository,
      lockRewardsOnChainTransport: lockRewardsNamespace.Transport,
      orchestratedQueryStore: this.OrchestratedQueryStore,
      tokenLookup: this.Tokens.Repository,
      wallet: this.Wallet.Connection,
      lockOnChainTransport,
      voteOnChainTransport,
    })

    // -- LOCK VENAMM QUOTES (Presentation layer for venamm quotes) --
    this.LockVenammQuotes = LockVenammQuotesNamespaceFactory.create({
      lockOnChainTransport,
      venammStore: venammBase.Store,
      tokenLookup: this.Tokens.Repository,
      lockRepository: this.Lock.Repository,
      eventBus: this.EventBus,
    })

    // Now complete Venamm namespace by adding Sell sub-namespace
    this.Venamm = VenammNamespaceFactory.addSellNamespace(venammBase, {
      chainId,
      config,
      voterAddress: this.scaleAppContractAddresses.voter,
      veTokenAddress: this.scaleAppContractAddresses.veToken,
      venAmmAddress: this.scaleAppContractAddresses.venAmm,
      wallet: this.Wallet.Connection,
      lockOnChainTransport,
      tokenLookup: this.Tokens.Repository,
      venammStore: venammBase.Store,
      eventBus: this.EventBus,
      venammQuoteService: this.LockVenammQuotes.Service,
    })

    // Create SelectedLockOrchestrator for managing selected lock state
    const selectedLockOrchestrator = new SelectedLockOrchestrator(this.EventBus)

    // Create WithdrawOrchestrator for lock page
    const withdrawOrchestrator = WithdrawOrchestratorFactory.create(
      chainId,
      config,
      null, // Start with no lock - will be updated when NFT is selected
      this.EventBus,
      this.scaleAppContractAddresses.voter,
      this.scaleAppContractAddresses.veToken,
      this.Wallet.Connection,
    )

    // Now create LockPageStore with the orchestrators (after Lock namespace is created)
    this.Pages.Lock = new LockPageStore(
      localStorageBackend,
      urlParamsBackend,
      this.chainSlug,
      withdrawOrchestrator,
      selectedLockOrchestrator,
      this.Lock.Repository,
    )
    this.Pages.Lock.initialize()

    // Create Manager namespace
    this.Manager = ManagerNamespaceFactory.create({
      config,
      publicClientGetter,
      eventBus: this.EventBus,
      lockOnChainTransportAddresses,
      lockRewardRepository: lockRewardsNamespace.Repository,
      lockRewardsOnChainTransport: lockRewardsNamespace.Transport,
      orchestratedQueryStore: this.OrchestratedQueryStore,
      tokenLookup: this.Tokens.Repository,
      wallet: this.Wallet.Connection,
      storage: localStorageBackend,
      chainSlug: this.chainSlug,
      chainId,
      superVoterAddress: this.scaleAppContractAddresses.superVoter,
      venammStore: this.Venamm.Store,
      voteOnChainTransport,
    })

    // Create WalletMenuStore after Manager is created (needs ManagerStore)
    this.Wallet.Menu = new WalletMenuStore(this.Wallet.Connection, this.Manager.Store, this.EventBus)

    const minimalPoolOnChainTransportAddresses: MinimalPoolOnChainTransportAddresses = {
      scalensDefaultInfoPairs: this.scaleAppContractAddresses.scalensDefaultInfoPairs
        ? new LcAddress(this.scaleAppContractAddresses.scalensDefaultInfoPairs)
        : undefined,
      scalensUserInfoPairs: this.scaleAppContractAddresses.scalensUserInfoPairs
        ? new LcAddress(this.scaleAppContractAddresses.scalensUserInfoPairs)
        : undefined,
      scalensUserManageMinimalPoolInfo: this.scaleAppContractAddresses.scalensUserManageMinimalPoolInfo
        ? new LcAddress(this.scaleAppContractAddresses.scalensUserManageMinimalPoolInfo)
        : undefined,
    }

    // Create MinimalPoolOnChainTransport with strategy
    const minimalPoolOnChainTransport = new MinimalPoolOnChainTransport(
      config,
      minimalPoolOnChainTransportAddresses,
      chunkedRpcStrategy,
    )

    // Create Gauge namespace (before MinimalPools for bidirectional mapping)
    const gaugeOnChainTransportAddresses = {
      voter: this.scaleAppContractAddresses.voter,
    }
    this.Gauge = GaugeNamespaceFactory.create({
      config,
      chainId,
      gaugeOnChainTransportAddresses,
      orchestratedQueryStore: this.OrchestratedQueryStore,
      apiService,
      eventBus: this.EventBus,
      minimalPoolRepository: undefined, // Will be set after MinimalPools is created
      clFarmRepository: undefined, // Will be set after ClFarms is created
      emissionsConfig: this.emissionsConfig,
      tokenLookup: this.Tokens.Repository,
      lockLookup: this.Lock.Repository,
    })

    this.MinimalPools = buildMinimalPoolNamespace(
      chainId,
      apiService,
      config,
      this.OrchestratedQueryStore,
      this.Tokens.Repository,
      this.Wallet.Connection,
      minimalPoolOnChainTransport,
      new LcAddress(routerAddress),
      this.EventBus,
      this.Gauge.Repository, // Pass gauge repository for bidirectional mapping
      appConfig.bannedPairs,
    )

    // Create Vote Page namespace
    const wrappedNativeToken = this.Tokens.Repository.find(new LcAddress(systemTokenAddresses.wrappedNative))
    this.Pages.Vote = VotePageNamespaceFactory.create({
      storage: localStorageBackend,
      urlParams: urlParamsBackend,
      chainSlug: this.chainSlug,
      gaugeRepository: this.Gauge.Repository,
      voteStore: this.Vote.Store,
      userLockRepository: this.Lock.Repository,
      managerLockRepository: this.Manager.Lock.Repository,
      managerStore: this.Manager.Store,
      nativeTokenSymbol: systemTokenAddresses.native.symbol,
      wrappedNativeTokenSymbol: wrappedNativeToken?.symbol ?? "WETH",
      lockLookup: this.Lock.Repository, // ILockRepository implements ILockLookup
      emissionsConfig: this.emissionsConfig,
      orchestratedQueryStore: this.OrchestratedQueryStore,
      walletStore: this.Wallet.Connection,
      eventBus: this.EventBus,
      config: this.config,
      chainId: this.chainId,
      veNftAddress: this.scaleAppContractAddresses.veToken,
      superVoterAddress: this.scaleAppContractAddresses.superVoter,
    })

    // Create Strategy Manager namespace (event-driven strategy calculation)
    // Uses SelectedPowerStore from VotePage, GaugeRepository, and LockRepository
    this.VoteOptimizer = StrategyManagerNamespaceFactory.create({
      gaugeRepository: this.Gauge.Repository,
      selectedVotePowerProvider: this.Pages.Vote.SelectedPowerStore,
      lockLookup: this.Lock.Repository,
      eventBus: this.EventBus,
    })

    // Inject StrategyManagerStore into SuperVoterAccessOrchestrator
    // This is done after VoteOptimizer creation to resolve the circular dependency
    this.Pages.Vote.Orchestrator.setStrategyManagerStore(this.VoteOptimizer.Store)

    // -- POOLS & CL FARMS (using DI to resolve circular dependency) --
    // Bind Pools with its dependencies
    // @todo - do we still need this now that we have resolved the circular dependency?
    // CL Farms no longer needs access to pools
    // and I don't know WHY pools needs access to CLFarms.. ?
    // I think we were half way through a feature whe we hit the problem but I pivoted so this is no longer an issue..
    // it would be nice to remove this if not needed
    container
      .bind<Pools>(TYPES.Pools)
      .toDynamicValue(() => {
        return new Pools(this.MinimalPools.Repository, container.get<ClFarmRepository>(TYPES.ClFarmRepository))
      })
      .inSingletonScope()

    const clFarmOnChainTransportAddresses: ClFarmOnChainTransportAddresses = {
      scalensClFarms: new LcAddress(this.scaleAppContractAddresses.clFarmLensAddress),
    }

    // Create ClFarmOnChainTransport with strategy
    const clFarmOnChainTransport = new ClFarmOnChainTransport(
      () => this.getPublicClient(),
      clFarmOnChainTransportAddresses,
      chunkedRpcStrategy,
    )

    this.ClFarms = buildClFarmNamespace(
      apiService,
      clFarmOnChainTransport,
      container.get<ClFarmRepository>(TYPES.ClFarmRepository),
      this.OrchestratedQueryStore,
      this.Tokens.Repository,
      this.Wallet.Connection,
      this.Log,
      this.EventBus,
      this.Gauge.Repository, // Pass gauge repository for bidirectional mapping
      new LcAddress(systemTokenAddresses.governance), // Pass governance token address
    )
    this.ClFarms.Orchestrator?.query?.start()

    this.Stats = buildScaleStatsNamespace(apiService, this.OrchestratedQueryStore)

    // Create ManualClGraphQlService for the thick-v1 Manual CL domain.
    // `getThickV1GraphQlUrl` resolves the full URL from either a direct
    // `VITE_THICK_V1_GRAPHQL_URL` override (local dev against the raw
    // indexer) or the branch-specific scale-proxy base + domain path.
    const thickV1GraphQlUrl = getThickV1GraphQlUrl(appConfig.branch)
    if (import.meta.env.DEV) {
      console.info("[thick-v1] GraphQL URL:", thickV1GraphQlUrl)
    }
    const manualClGraphQlService = new ManualClV1GraphQlService({
      url: thickV1GraphQlUrl,
      safelyFetchGraphQl,
    })

    // Create Manual CL namespace (thick-v1). A future thick-v2 copy wires
    // an additional `this.ManualClV2` alongside this field.
    this.ManualClV1 = ManualClV1NamespaceFactory.create({
      graphQlService: manualClGraphQlService,
      tokenRepository: this.Tokens.Repository,
      orchestratedQueryStore: this.OrchestratedQueryStore,
      eventBus: this.EventBus,
      chainId: this.chainId,
      config: this.config,
      wallet: this.Wallet.Connection,
      approvalModeProvider: this.Settings,
      chunkedRpcStrategy,
    })

    const thickV2GraphQlUrl = getThickV2GraphQlUrl(appConfig.branch)
    if (import.meta.env.DEV) {
      console.info("[thick-v2] GraphQL URL:", thickV2GraphQlUrl)
    }
    const manualClV2GraphQlService = new ManualClV2GraphQlService({
      url: thickV2GraphQlUrl,
      safelyFetchGraphQl,
    })

    this.ManualClV2 = ManualClV2NamespaceFactory.create({
      graphQlService: manualClV2GraphQlService,
      tokenRepository: this.Tokens.Repository,
      orchestratedQueryStore: this.OrchestratedQueryStore,
      eventBus: this.EventBus,
      chainId: this.chainId,
      config: this.config,
      wallet: this.Wallet.Connection,
      approvalModeProvider: this.Settings,
      chunkedRpcStrategy,
    })

    // Create EpochGraphQlService for Epochs domain
    const epochGraphQlBaseUrl = getEpochGraphQlBaseUrl(appConfig.branch)
    const epochGraphQlService = new EpochGraphQlService({
      baseUrl: epochGraphQlBaseUrl,
      safelyFetchGraphQl,
    })

    // Create Epochs namespace
    this.Epochs = EpochNamespaceFactory.create({
      graphQlService: epochGraphQlService,
      orchestratedQueryStore: this.OrchestratedQueryStore,
      eventBus: this.EventBus,
    })

    // Start current epoch query immediately (always runs)
    this.Epochs.Orchestrator.currentEpochQuery?.start()

    const heroEmissionsStore = new HeroEmissionsStore()
    const heroEmissionsQuery = new OrchestratedQuery<HeroEmissionsAnalyticsData>(
      "Hero Emissions Analytics",
      async () => {
        const response = await apiService.getV4EmissionsAnalytics()
        return response as HeroEmissionsAnalyticsData
      },
      300_000,
      (_sender, data) => {
        runInAction(() => {
          heroEmissionsStore.updateFromAnalytics(data)
        })
      },
      (_sender, error) => {
        console.error("[ScaleApp] Hero emissions analytics query failed:", error)
      },
      this.OrchestratedQueryStore,
    )
    heroEmissionsQuery.start()

    // Create HeroBlockStats (depends on Stats + Epochs + emissions analytics fallback)
    this.HeroBlockStats = new HeroBlockStatsStore(
      this.Stats,
      this.Epochs.Repository,
      this.Epochs.Orchestrator,
      heroEmissionsStore,
      this.chainSlug,
    )

    // Live activity ticker: poll the thick-v2 indexer for recent global swaps
    // and push normalized rows into the store the homepage observes.
    this.LiveActivity = new LiveActivityStore()
    const liveActivityQuery = new OrchestratedQuery<RecentSwapActivity[]>(
      "Live Activity Feed",
      async () => manualClV2GraphQlService.getRecentActivity(12),
      30_000,
      (_sender, data) => {
        const rows: LandingActivityRow[] = data
          .filter((swap) => swap.txHash !== "" && swap.amountUSD > 0)
          .map((swap) => ({
            id: swap.id,
            valueUsd: swap.amountUSD,
            account: swap.account,
            timestampSec: swap.timestampSec,
            txUrl: `https://basescan.org/tx/${swap.txHash}`,
          }))
        runInAction(() => {
          this.LiveActivity.updateRows(rows)
        })
      },
      (_sender, error) => {
        console.error("[ScaleApp] Live activity feed query failed:", error)
      },
      this.OrchestratedQueryStore,
    )
    liveActivityQuery.start()

    // Create Blog namespace
    this.Blog = BlogNamespaceFactory.create()

    // Create Docs namespace
    this.Docs = DocsNamespaceFactory.create()

    // Make the App observable
    makeObservable(this, {
      Blog: observable,
      ClFarms: observable,
      Docs: observable,
      EventBus: observable,
      Epochs: observable,
      HeroBlockStats: observable,
      LiveActivity: observable,
      Lock: observable,
      LockRewards: observable,
      Log: observable,
      ManualClV1: observable,
      ManualClV2: observable,
      MinimalPools: observable,
      OrchestratedQueryStore: observable,
      Pages: observable,
      PendingCreation: observable,
      Settings: observable,
      Stats: observable,
      Tokens: observable,
      Wallet: observable,
    })

    this.PendingCreation.clearStale()

    console.log("🚀 Application initialized with MobX + OOP architecture and injected blockchain clients")
  }

  // #region Static Methods
  public static initialize(props: InitializeScaleApplicationParams): ScaleApp | undefined {
    // Early returns if dependencies aren't ready
    if (!props.apiService) return
    if (ScaleApp.instance) return ScaleApp.instance

    ScaleApp.instance = new ScaleApp(
      props.apiService,
      props.safeAddress,
      props.chainSlug,
      props.scaleAppContractAddresses,
      props.config,
      props.chainId,
      props.systemTokenAddresses,
      props.routerAddress,
      props.emissionsConfig,
    )

    return ScaleApp.instance
  }

  public static getInstance(): ScaleApp {
    if (!ScaleApp.instance) {
      throw new Error("App must be initialized first")
    }
    return ScaleApp.instance
  }

  public getApiService(): ApiService {
    return this.apiService
  }

  public getChainSlug(): string {
    return this.chainSlug
  }

  public getPublicClient(): PublicClient {
    const client = getPublicClient(this.config)
    if (!client) {
      throw new Error("Failed to get public client from wagmi config")
    }
    return client
  }

  public getWagmiConfig(): Config {
    return this.config
  }

  public connectAccount(
    address: Address,
    connectorId?: string | null,
    connectorName?: string | null,
    connectorType?: string | null,
  ): void {
    if (!address || typeof address !== "string") {
      console.warn("connectAccount called with invalid address:", address)
      return
    }
    this.Wallet.Connection.connectWallet(address, connectorId, connectorName, connectorType)
  }

  public disconnectAccount(): void {
    // Clear strategy state before disconnecting wallet
    this.clearStrategyState()
    this.Wallet.Connection.disconnectWallet()
  }

  /**
   * Clear all strategy state when wallet disconnects
   */
  public clearStrategyState(): void {
    // Clear selection state in all strategies
    this.LockRewards.Strategies.TokenSelection.clear()
    this.LockRewards.Strategies.NftSelection.clear()

    // Note: Grouping strategies are stateless, so no need to clear them
  }

  // #endregion Static Methods
}

// -- SCALE STATS --
const buildScaleStatsNamespace = (
  apiService: ApiService,
  orchestratedQueryStore: OrchestratedQueryStore,
): ScaleStatsStore => {
  const scaleStatsApiTransport = new ScaleStatsApiTransport(apiService)
  const scaleStatsStore = new ScaleStatsStore()
  const scaleStatsOrchestrator = new ScaleStatsOrchestrator(scaleStatsStore, scaleStatsApiTransport)

  // Create and initialize the stats sync query
  const loadStatsQuery = new OrchestratedQuery<StatsEqualizerResponse>(
    "Scale Stats Sync",
    (query) => scaleStatsOrchestrator.onLoadStatsTrigger(query),
    30_000,
    scaleStatsOrchestrator.onLoadStatsQuerySuccess.bind(scaleStatsOrchestrator),
    scaleStatsOrchestrator.onLoadStatsQueryFailed.bind(scaleStatsOrchestrator),
    orchestratedQueryStore,
  )

  scaleStatsOrchestrator.initialize(loadStatsQuery)
  loadStatsQuery.start()

  return scaleStatsStore
}

// -- CL FARMS --
type ClFarmNamespace = {
  Orchestrator: ClFarmOrchestrator
  Repository: ClFarmRepository
}

const buildClFarmNamespace = (
  apiService: ApiService,
  clFarmOnChainTransport: ClFarmOnChainTransport,
  clFarmRepository: ClFarmRepository,
  orchestratedQueryStore: OrchestratedQueryStore,
  tokenRepository: TokenRepository,
  wallet: WalletConnectionStore,
  log: LogStore,
  eventBus: IEventBus,
  gaugeRepository: IGaugeRepository | undefined,
  governanceTokenAddress: LcAddress,
): ClFarmNamespace => {
  const clFarmApiTransport = new ClFarmApiTransport(apiService)
  const clFarmOrchestrator = ClFarmOrchestratorFactory.create(
    clFarmApiTransport,
    clFarmOnChainTransport,
    clFarmRepository,
    orchestratedQueryStore,
    tokenRepository,
    wallet,
    log,
    eventBus,
    gaugeRepository,
    governanceTokenAddress,
  )

  return { Repository: clFarmRepository, Orchestrator: clFarmOrchestrator }
}

// -- LOCK REWARDS --
type LockRewardsNamespace = {
  Repository: LockRewardRepository
  Transport: LockRewardsOnChainTransport // Internal only, not exposed publicly
}

const buildLockRewardsNamespace = (
  config: Config,
  lockRewardsOnChainTransportAddresses: LockRewardsOnChainTransportAddresses,
): LockRewardsNamespace => {
  const lockRewardRepository = new LockRewardRepository()
  const lockRewardsOnChainTransport = new LockRewardsOnChainTransport(config, lockRewardsOnChainTransportAddresses)

  return { Repository: lockRewardRepository, Transport: lockRewardsOnChainTransport }
}

// -- POOLS --
const buildPoolsNamespace = (
  minimalPoolRepository: MinimalPoolRepository,
  clFarmRepository: ClFarmRepository,
): Pools => {
  return new Pools(minimalPoolRepository, clFarmRepository)
}

// -- MINIMAL POOLS --
type MinimalPoolNamespace = {
  Orchestrator: MinimalPoolOrchestrator
  Repository: MinimalPoolRepository
  ZapOrchestrator: ZapOrchestrator
}

const buildMinimalPoolNamespace = (
  chainId: number,
  apiService: ApiService,
  config: Config,
  orchestratedQueryStore: OrchestratedQueryStore,
  tokenRepository: TokenRepository,
  wallet: WalletConnectionStore,
  minimalPoolOnChainTransport: MinimalPoolOnChainTransport,
  routerAddress: LcAddress,
  eventBus: IEventBus,
  gaugeRepository: any | undefined,
  bannedPairs: ChainConfig["bannedPairs"],
): MinimalPoolNamespace => {
  const minimalPoolApiTransport = new MinimalPoolApiTransport(apiService)
  const minimalPoolRepository = new MinimalPoolRepository()
  const minimalPoolOrchestrator = MinimalPoolOrchestratorFactory.create(
    minimalPoolApiTransport,
    minimalPoolRepository,
    tokenRepository,
    wallet,
    orchestratedQueryStore,
    minimalPoolOnChainTransport,
    routerAddress,
    eventBus,
    gaugeRepository,
    bannedPairs,
  )

  const zapOrchestrator = ZapOrchestratorFactory.create(chainId, config, wallet)

  return { Repository: minimalPoolRepository, Orchestrator: minimalPoolOrchestrator, ZapOrchestrator: zapOrchestrator }
}

// -- TOKENS --
type TokenNamespaceBase = {
  Orchestrator: TokenOrchestrator
  Repository: TokenRepository
  QuickSelect: QuickSelectTokenStore
}

const buildTokenNamespace = (
  apiService: ApiService,
  tokenOnChainTransport: TokenOnChainTransport,
  systemTokenAddresses: SystemTokenConfig,
  orchestratedQueryStore: OrchestratedQueryStore,
  wallet: WalletConnectionStore,
  eventBus: IEventBus,
): TokenNamespaceBase => {
  const tokenApiTransport = new TokenApiTransport(apiService)
  const tokenRepository = new TokenRepository(systemTokenAddresses)
  const tokenOrchestrator = TokenOrchestratorFactory.create(
    tokenApiTransport,
    tokenOnChainTransport,
    tokenRepository,
    orchestratedQueryStore,
    wallet,
    isTokenBlacklistChecker,
    eventBus,
  )

  // Create QuickSelectTokenStore after static tokens are loaded
  // Static tokens are loaded in TokenOrchestratorFactory.create()
  const quickSelectTokenStore = QuickSelectTokenStoreFactory.create()

  return {
    Orchestrator: tokenOrchestrator,
    Repository: tokenRepository,
    QuickSelect: quickSelectTokenStore,
  }
}
